import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, AlertCircle } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1553413077-190dd305871c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

export default function LoginView() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const fillDemo = () => {
    setEmail("pt@epgroup.co.uk");
    setPassword("Myton2027!");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-slate-900">
      {/* Brand panel */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="EP Group warehouse and logistics facility" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/92 via-[#3d0f12]/80 to-slate-900/85" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <img src="/ep-logo.png" alt="EP Group — 50 years of delivering excellence" className="h-11 w-auto self-start" />
          <div>
            <h1 className="font-display font-bold text-4xl leading-tight mb-4">
              Financial Reconciliation Hub
            </h1>
            <p className="text-slate-300 max-w-md">
              Maintain depot rate cards, bulk-upload updates with duplicate checking, and browse the 4-4-5 financial
              calendar through FY31.
            </p>
          </div>
          <p className="text-xs text-slate-400">© {new Date().getFullYear()} EP Group · Financial Reconciliation Hub</p>
        </div>
      </div>

      {/* Credentials */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-white dark:bg-slate-950">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <img src="/ep-logo-mark.png" alt="EP Group" className="h-8 w-auto" />
            <span className="font-display font-bold text-xl text-slate-900 dark:text-white">Hub</span>
          </div>
          <h2 className="font-display font-bold text-2xl text-slate-900 dark:text-white mb-1">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-8">Sign in to manage your rate cards and calendar.</p>

          {error && (
            <div className="flex items-start gap-2 mb-5 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@epgroup.co.uk"
                required
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5"
              />
            </div>
            <Button type="submit" data-testid="login-submit-button" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <button
            type="button"
            onClick={fillDemo}
            data-testid="login-demo-credentials-button"
            className="mt-5 w-full text-center text-sm text-[#d4333b] hover:text-[#b82a31] hover:underline"
          >
            Use demo credentials
          </button>
        </div>
      </div>
    </div>
  );
}
